import { CaptureContextLinks, ImageWithLoader, ModalTitle } from '@overlayqa/shared/ui';
import { useState, useRef, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Camera01Icon, Maximize01Icon, CancelCircleIcon } from '@hugeicons/core-free-icons';
import { sanitizeImgSrc } from '@overlayqa/shared';
import { resolveAssetUrl } from '@/lib/api/client';
import { SegmentedControl } from '@/components/ui/SegmentedControl';

interface ScreenshotCardProps {
  clientReviewVersion?: import('@overlayqa/shared').ClientReviewVersionProvenance | null;
  screenshotUrl?: string;
  /** elementData.comparisonContext: the reference and Figma frame links render under the images. */
  comparisonContext?: unknown;
  /**
   * Uncropped full-page context shot (extension capture + client-review
   * bridge). Shown on a "Page context" tab beside the element crop so the team sees WHERE on the
   * page the issue points. If this is the only saved image, show it directly.
   */
  fullPageScreenshotUrl?: string;
}

/**
 * One image + its own fullscreen lightbox (a native <dialog>). Used for the
 * element crop and the full-page context shot; each owns its expand state.
 */
function ExpandableShot({ url, alt }: { url: string; alt: string }) {
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const open = useCallback(() => {
    setExpanded(true);
    dialogRef.current?.showModal();
  }, []);
  const close = useCallback(() => {
    dialogRef.current?.close();
    setExpanded(false);
  }, []);

  return (
    <>
      <div className="relative group mt-4">
        <ImageWithLoader
          src={resolveAssetUrl(sanitizeImgSrc(url))}
          alt={alt}
          // max-w-full (not w-full): w-full UPSCALED small element crops
          // (e.g. a ~788px-wide focal crop) to the full card width,
          // rendering them blurry and "zoomed in" (user report
          // 2026-06-11). Natural size, shrink-only, centered.
          className="rounded-lg max-w-full mx-auto max-h-[432px] object-contain cursor-pointer"
          onClick={open}
        />
        <button
          type="button"
          onClick={open}
          className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label={`Expand ${alt.toLowerCase()}`}
        >
          <HugeiconsIcon icon={Maximize01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" />
        </button>
      </div>

      <dialog
        ref={dialogRef}
        aria-label={alt}
        className="fixed inset-0 w-full h-full max-w-none max-h-none m-0 p-0 bg-transparent backdrop:bg-black/80"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        onClose={() => setExpanded(false)}
      >
        {expanded && (
          <div className="flex items-center justify-center w-full h-full px-8 pt-16 pb-8">
            <ModalTitle icon="image" tone="on-dark" className="fixed top-4 left-4 max-w-[calc(100%_-_5rem)] rounded-md bg-black/60 px-3 py-2 text-sm text-white">{alt}</ModalTitle>
            <button
              type="button"
              onClick={close}
              className="fixed top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
              aria-label="Close"
            >
              <HugeiconsIcon icon={CancelCircleIcon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5" />
            </button>
            <ImageWithLoader
              frameStyle={{ maxWidth: '100%', height: '100%' }}
              src={resolveAssetUrl(sanitizeImgSrc(url))}
              alt={`${alt} (expanded)`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        )}
      </dialog>
    </>
  );
}

/**
 * Issue screenshot card + fullscreen lightbox. Owns its own expand/collapse
 * state (a native <dialog>); the page just passes the URLs. Shows the element
 * crop first, then the full-page "Page context" shot when the capture stored
 * one — without it, a tight crop gives the team no idea where on the page the
 * issue lives ("no website shown", user report 2026-07-13).
 */
export function ScreenshotCard({ screenshotUrl, fullPageScreenshotUrl, comparisonContext, clientReviewVersion }: ScreenshotCardProps) {
  const [view, setView] = useState<'element' | 'page'>('element');
  const hasBothViews = Boolean(screenshotUrl && fullPageScreenshotUrl);
  return (
    <section id="issue-screenshot" className="card-sm">
      <h2 className="section-label mb-3 flex items-center gap-2">
        <HugeiconsIcon icon={Camera01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
        Screenshot
      </h2>
      {hasBothViews && (
        <SegmentedControl
          aria-label="Screenshot view"
          size="sm"
          idPrefix="issue-shot"
          className="mb-3 w-fit"
          value={view}
          onChange={setView}
          options={[{ value: 'element', label: <span className="whitespace-nowrap">Screenshot</span> }, { value: 'page', label: <span className="whitespace-nowrap">Page context</span> }]}
        />
      )}
      {(view === 'page' || !screenshotUrl) && fullPageScreenshotUrl ? (
        <div role={hasBothViews ? 'tabpanel' : undefined} id="issue-shot-page-panel" aria-labelledby={hasBothViews ? 'issue-shot-page-tab' : undefined}>
          <ExpandableShot url={fullPageScreenshotUrl} alt="Page context" />
        </div>
      ) : screenshotUrl ? (
        <ExpandableShot url={screenshotUrl} alt="Screenshot" />
      ) : (
        // mx-auto: aspect-video + max-h resolves to a 768x432 box, which is
        // narrower than this card on a wide page, so without it the
        // placeholder hugs the left edge while the real image one branch above
        // has been centred since 2026-06-11.
        <div className="bg-gray-100 rounded-lg aspect-video flex items-center justify-center text-gray-400 mt-4 max-h-[432px] mx-auto">
          No screenshot available
        </div>
      )}
      {/* The comparison's reference and Figma frame sit under the images,
          inside the card, as on team review and the extension. */}
      {comparisonContext || clientReviewVersion ? <div className="mt-3"><CaptureContextLinks comparisonContext={comparisonContext} clientReviewVersion={clientReviewVersion} /></div> : null}
    </section>
  );
}
